import React, { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { m as motion, useMotionValue } from 'framer-motion';
import { createCSSTransition, MotionDurations, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { useFocusGroup } from '@polestar/component-warehouse-react/hooks/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
/**
 * @internal
 */
export const galleryMaximumContentLength = 7;
const indicatorSizes = {
    indicatorSize: 8,
    indicatorSizeMobile: 6,
    gap: 4,
};
const calcGalleryIndicatorSize = (length) => {
    const { indicatorSize, gap } = indicatorSizes;
    return (indicatorSize + gap) * length - gap / 2;
};
/**
 * @internal
 */
export var GalleryIndicatorCSSCustomProperties;
(function (GalleryIndicatorCSSCustomProperties) {
    GalleryIndicatorCSSCustomProperties["flexDirection"] = "--cw-internal-gallery-indicator-flex-direction";
})(GalleryIndicatorCSSCustomProperties || (GalleryIndicatorCSSCustomProperties = {}));
const GalleryIndicator = forwardRef(({ id, length, activeIndex: _activeIndex, onUpdateIndex, buttonProps = {}, isMobileOverflow = false, ariaButtonLabel, orientation = Orientations.horizontal, ...props }, ref) => {
    const memoizedId = useSSRSafeId('gallery-indicator', id);
    const internalRef = useRef();
    const combinedRef = useCombinedRef(internalRef, ref);
    const [internalActiveIndex, setInternalActiveIndex] = useState(typeof _activeIndex === 'number' ? _activeIndex : _activeIndex.get());
    const _internalActiveIndex = typeof _activeIndex === 'number' ? _activeIndex : internalActiveIndex;
    useEffect(() => {
        if (typeof _activeIndex !== 'number') {
            return _activeIndex.on('change', setInternalActiveIndex);
        }
    }, [_activeIndex]);
    const { setFocusIndex, createFocusProps } = useFocusGroup({
        ref: internalRef,
        initialFocusIndex: _internalActiveIndex,
        orientation,
    });
    useEffect(() => {
        setFocusIndex(_internalActiveIndex);
    }, [_internalActiveIndex, setFocusIndex]);
    const emptyList = Array(length).fill(null).slice(0, galleryMaximumContentLength);
    const tabIds = emptyList.map((tab, index) => `${memoizedId}-${index}`);
    // Start logic for mobile motion
    const containerOffset = useMotionValue(0);
    const { name } = useMediaQuery();
    const isMobile = name === DeviceTypes.mobile;
    const indicatorSize = (isMobile ? indicatorSizes.indicatorSizeMobile : indicatorSizes.indicatorSize) +
        (isMobile ? indicatorSizes.gap : indicatorSizes.gap);
    useEffect(() => {
        if (isMobile && isMobileOverflow) {
            if (_internalActiveIndex < 4) {
                containerOffset.set(0);
            }
            if (_internalActiveIndex === 4 && length > 5) {
                containerOffset.set(-indicatorSize);
            }
            if (_internalActiveIndex >= 5) {
                length > 6
                    ? containerOffset.set(-indicatorSize * 2)
                    : containerOffset.set(-indicatorSize);
            }
        }
        else {
            containerOffset.set(0);
        }
    }, [_internalActiveIndex, length, isMobile, containerOffset, indicatorSize, isMobileOverflow]);
    const handleClick = useCallback((e) => {
        onUpdateIndex === null || onUpdateIndex === void 0 ? void 0 : onUpdateIndex(e);
    }, [onUpdateIndex]);
    return (React.createElement(Container, { ...aria({
            role: 'tablist',
            owns: tabIds.join(' '),
        }), id: memoizedId, ...props, ref: combinedRef, orientation: orientation },
        React.createElement(MotionWrapper, { style: {
                [orientation === Orientations.horizontal ? 'x' : 'y']: containerOffset,
                transitionDelay: `${MotionDurations.none}ms`,
                transitionDuration: `${MotionDurations.slow}ms`,
            }, orientation: orientation }, emptyList.map((item, index) => (React.createElement(GalleryIndicatorButton, { id: tabIds[index], key: index, isMobileOverflow: isMobileOverflow, index: index, type: "button", ...aria({
                label: (ariaButtonLabel === null || ariaButtonLabel === void 0 ? void 0 : ariaButtonLabel({
                    number: index + 1,
                    current: _internalActiveIndex === index,
                })) || `Indicator ${index + 1}`,
                selected: _internalActiveIndex === index,
                role: 'tab',
            }), ...buttonProps, active: _internalActiveIndex === index, onClick: handleClick, orientation: orientation, ...createFocusProps(index) }))))));
});
GalleryIndicator.displayName = 'GalleryIndicator';
export default GalleryIndicator;
const GalleryIndicatorButton = memo(forwardRef(({ index, active, onClick, orientation, isMobileOverflow = false, ...props }, ref) => {
    const handleClick = useCallback(() => {
        onClick(index);
    }, [index, onClick]);
    const dotAnimation = {
        initial: { opacity: 0, scale: 0.4 },
        whileInView: { opacity: 1, scale: 1 },
        noAnimation: { opacity: 1, scale: 1 },
    };
    return (React.createElement(GalleryIndicatorButtonWrapper, { ...props, key: `indicator-button-${index}`, ref: ref, active: active, onClick: handleClick, orientation: orientation },
        React.createElement(GalleryIndicatorDot, { key: `indicator-button-${index}-background`, transition: { duration: 0, delay: 0 }, initial: !isMobileOverflow ? 'noAnimation' : 'initial', whileInView: !isMobileOverflow ? 'noAnimation' : 'whileInView', exit: !isMobileOverflow ? 'noAnimation' : 'initial', variants: dotAnimation, active: active, orientation: orientation })));
}));
GalleryIndicatorButton.displayName = 'GalleryIndicatorButton';
const MotionWrapper = styled(motion.div, shouldNotForwardProps(true, 'orientation'))(({ orientation }) => css(css `
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex-direction: var(${GalleryIndicatorCSSCustomProperties.flexDirection});
      gap: ${createCustomSpacing(indicatorSizes.gap / 16)};
      position: relative;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      pointer-events: auto;

      @media (hover: none) {
        pointer-events: none;
      }
    `, orientation === Orientations.vertical &&
    css `
        flex-direction: column;
      `));
const Container = styled('div', shouldNotForwardProps(true, 'orientation'))(({ orientation }) => css(css `
      ${GalleryIndicatorCSSCustomProperties.flexDirection}: row;
      overflow: hidden;
    `, orientation === Orientations.horizontal &&
    css `
        width: ${calcGalleryIndicatorSize(galleryMaximumContentLength)}px;
      `, orientation === Orientations.vertical &&
    css `
        height: ${calcGalleryIndicatorSize(galleryMaximumContentLength)}px;
      `));
const GalleryIndicatorButtonWrapper = styled('button', shouldNotForwardProps(false, 'active', 'orientation'))(({ theme, active, orientation }) => css(css `
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      touch-action: manipulation;
      padding: 0;

      @media (hover: hover) {
        &:hover > div {
          background: ${!active
    ? theme.gallery.states.hover.indicatorDotColor
    : theme.gallery.states.selected.indicatorDotColor};
        }
      }
    `, orientation === Orientations.horizontal &&
    css `
        height: ${createCustomSpacing(30 / 16)};
        ${MediaQueries.desktop} {
          height: ${createCustomSpacing(56 / 16)};
        }
      `, orientation === Orientations.vertical &&
    css `
        width: ${createCustomSpacing(30 / 16)};
        ${MediaQueries.desktop} {
          width: ${createCustomSpacing(56 / 16)};
        }
      `));
const GalleryIndicatorDot = styled(motion.div, shouldNotForwardProps(false, 'active', 'orientation'))(({ active, orientation, theme }) => css(css `
      /* Needs to be all for the transition to work correctly (we are changing background and outline) */
      transition: ${createCSSTransition('all')};
      background: ${active
    ? theme.gallery.states.selected.indicatorDotColor
    : theme.gallery.states.enabled.indicatorDotColor};
      outline: ${active ? '0px solid transparent' : '1px solid transparent'};
    `, orientation === Orientations.horizontal &&
    css `
        ${responsiveStyles({
        height: {
            mobile: createCustomSpacing(indicatorSizes.indicatorSizeMobile / 16),
            desktop: createCustomSpacing(indicatorSizes.indicatorSize / 16),
        },
        width: {
            mobile: createCustomSpacing(indicatorSizes.indicatorSizeMobile / 16),
            desktop: createCustomSpacing(indicatorSizes.indicatorSize / 16),
        },
    })}
      `, orientation === Orientations.vertical &&
    css `
        ${responsiveStyles({
        height: {
            mobile: createCustomSpacing(indicatorSizes.indicatorSizeMobile / 16),
            desktop: createCustomSpacing(indicatorSizes.indicatorSize / 16),
        },
        width: {
            mobile: createCustomSpacing(indicatorSizes.indicatorSizeMobile / 16),
            desktop: createCustomSpacing(indicatorSizes.indicatorSize / 16),
        },
    })}
      `));
